/* eslint-disable react/jsx-props-no-spreading */
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import {
  Box,
  Button,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
  type ButtonProps,
  type TooltipProps,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import clsx from "clsx";

const PREFIX = "ButtonDropdown";

const classes = {
  menuItem: `${PREFIX}-menuItem`,
  menuSelected: `${PREFIX}-menuSelected`,
  ButtonDropDown: `${PREFIX}-ButtonDropDown`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.menuItem}`]: {
    textTransform: "capitalize !important",
  },

  [`& .${classes.menuSelected}`]: {
    background: `${theme.palette.background.selected} !important`,
  },

  [`& .${classes.ButtonDropDown}`]: {
    ...theme.typography.body1,
    textTransform: "none",
  },
}));

type CustomButtonDropdownProps = {
  options: Map<string, string>;
  selectedValue: string;
  btncolor?: ButtonProps["color"];
  optionDisabled?: (option: [string, string]) => boolean;
  getOptionValue?: (option: string) => string;
  onChange: (value: string) => void;
  width?: number | string;
  translationNs?: any;
  placement?: TooltipProps["placement"];
  "data-testid"?: string;
  // `onChange` is omitted from ButtonProps: intersecting it with the DOM
  // FormEventHandler above yields a signature no caller can satisfy.
} & Omit<ButtonProps, "onChange">;

export function ButtonDropdown({
  options,
  selectedValue,
  color = "inherit",
  btncolor = "inherit",
  endIcon = null,
  optionDisabled = () => false,
  getOptionValue = (option: string) => option,
  style,
  disabled,
  onChange,
  width,
  className,
  translationNs,
  placement: forcedPlacement,
  ...restProps
}: CustomButtonDropdownProps) {
  const theme = useTheme();
  const { t } = useTranslation([translationNs]);
  const anchorRef = useRef();
  const [menuOpen, setOpen] = useState(false);

  function toggleMenuOpen() {
    setOpen((current) => !current);
  }

  function handleClose() {
    setOpen(false);
  }

  function handleSelectItem(_, value: string) {
    onChange(value);
    setOpen(false);
  }

  // eslint-disable-next-line no-nested-ternary
  const EndIcon = endIcon !== null ? endIcon : menuOpen ? <ArrowDropUp /> : <ArrowDropDownIcon />;

  return (
    <Root style={{ ...style }}>
      <Button
        ref={anchorRef}
        key="button"
        onClick={() => toggleMenuOpen()}
        endIcon={EndIcon}
        color={btncolor}
        variant="outlined"
        aria-controls={menuOpen ? "menu-list-grow" : undefined}
        aria-haspopup="true"
        className={clsx(classes.ButtonDropDown, className)}
        disabled={disabled}
        {...restProps}
      >
        {t(options.get(selectedValue), { ns: translationNs })
          ? t(options.get(selectedValue), { ns: translationNs })
          : t(selectedValue, { ns: translationNs }) || ""}
      </Button>
      <Popper
        open={menuOpen}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        key="popper"
        disablePortal
        sx={{ zIndex: theme.zIndex.drawer }}
        placement={forcedPlacement}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                (forcedPlacement ?? placement === "bottom") ? "center top" : "center bottom",
            }}
          >
            <Box component={Paper} width={width}>
              <ClickAwayListener onClickAway={() => handleClose()}>
                <MenuList
                  color={color}
                  sx={{ maxHeight: "400px", overflowY: options.size >= 15 && "scroll" }}
                >
                  {Array.from(options.entries()).map((option) => {
                    const isDisabled = optionDisabled(option);
                    return (
                      <MenuItem
                        key={getOptionValue(option[0])}
                        selected={option[0] === selectedValue}
                        disabled={isDisabled}
                        onClick={(event) => handleSelectItem(event, option[0])}
                        classes={{
                          root: classes.menuItem,
                          selected: classes.menuSelected,
                        }}
                        data-testid={`${restProps["data-testid"] || "dropdown-option"}__${t(
                          option[0],
                          { ns: translationNs }
                        )}`}
                      >
                        <Typography variant="body1" color="inherit">
                          {t(option[1], { ns: translationNs })}
                        </Typography>
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </ClickAwayListener>
            </Box>
          </Grow>
        )}
      </Popper>
    </Root>
  );
}
